/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useMemo, useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import "styled-components";
import { useMediaPredicate } from "react-media-hook";
import {
  PermissionListGroup,
  PermissionListHead,
  PermissionSelectorWrapper,
  ConfirmButton
} from "./PermissionSelector.styles";
import Button from "components/molecules/Button";
import Field from "components/molecules/Field";
import Grid from "components/molecules/Grid";
import GridCol from "components/molecules/GridCol";
import ModalContainer from "components/molecules/ModalContainer";
import DataTabs from "components/molecules/DataTabs";
import adminService from "services/adminService";
import Switch from "components/molecules/Switch";
import { FaArrowRightLong } from "react-icons/fa6";
import { MdInfoOutline, MdCheck } from "react-icons/md";
/* ─── helpers ──────────────────────────────────────────────────── */
function formatLabel(str = "") {
  return str
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getAccessLevel(suffix = "") {
  const map = {
    nav: "Navigation Access",
    add: "Create Access",
    edit: "Edit Access",
    delete: "Delete Access",
    view: "View Access",
    manage: "Manage Access",
  };
  return map[suffix] ?? formatLabel(suffix) + " Access";
}

/* ─── PermissionDetailsPanel (portal, fixed positioning) ─────────── */
function PermissionDetailsPanel({ permission, groupPermissions, selectedIds, onClose, anchorRect }) {
  const panelRef = useRef(null);

  const moduleName = formatLabel(permission.can.split(".")[0]);
  const suffix = permission.can.split(".").slice(1).join(".");
  const accessLevel = getAccessLevel(suffix);

  // group sibling permissions by their suffix category
  const actionGroups = useMemo(() => {
    const groups = {};
    groupPermissions.forEach((p) => {
      const parts = p.can.split(".");
      const cat = parts.length > 2 ? parts[1] : "general";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(p);
    });
    return groups;
  }, [groupPermissions]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  // Compute panel position from the anchor button's bounding rect
  const PANEL_WIDTH = 320;
  const OFFSET = 8;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let left = anchorRect ? anchorRect.left : 0;
  let top = anchorRect ? anchorRect.bottom + OFFSET : 0;

  // Flip left if panel would overflow viewport right edge
  if (left + PANEL_WIDTH > viewportWidth - 10) {
    left = Math.max(10, (anchorRect ? anchorRect.right : PANEL_WIDTH) - PANEL_WIDTH);
  }

  // Estimate panel height (~400px) — flip upward if not enough space below
  const APPROX_HEIGHT = 420;
  if (anchorRect && top + APPROX_HEIGHT > viewportHeight - 10) {
    top = Math.max(10, anchorRect.top - APPROX_HEIGHT - OFFSET);
  }

  const panel = (
    <div
      ref={panelRef}
      style={{
        position: "fixed",
        top,
        left,
        width: PANEL_WIDTH,
        zIndex: 99999,
        background: "#fff",
        borderRadius: "16px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.22)",
        padding: "20px",
        fontFamily: "inherit",
        display: "flex",
        flexDirection: "column",
        maxHeight: "min(420px, 90vh)",
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "18px", flexShrink: 0 }}>
        <MdInfoOutline style={{ fontSize: "20px", color: "#F97316" }} />
        <span style={{ fontWeight: 700, fontSize: "15px", color: "#111" }}>Permission Details</span>
      </div>

      {/* MODULE */}
      <div style={{ marginBottom: "12px", flexShrink: 0 }}>
        <div style={{ fontSize: "10px", fontWeight: 700, color: "#aaa", letterSpacing: "0.08em", marginBottom: "3px" }}>MODULE</div>
        <div style={{ fontSize: "14px", fontWeight: 600, color: "#111" }}>{moduleName}</div>
      </div>

      {/* PERMISSION KEY */}
      <div style={{ marginBottom: "12px", flexShrink: 0 }}>
        <div style={{ fontSize: "10px", fontWeight: 700, color: "#aaa", letterSpacing: "0.08em", marginBottom: "3px" }}>PERMISSION KEY</div>
        <div style={{
          display: "inline-block",
          background: "#F5F5F5",
          border: "1px solid #E5E7EB",
          borderRadius: "6px",
          padding: "4px 10px",
          fontFamily: "monospace",
          fontSize: "13px",
          color: "#333",
          wordBreak: "break-all",
        }}>
          {permission.can}
        </div>
      </div>

      {/* ACCESS LEVEL */}
      <div style={{ marginBottom: "12px", flexShrink: 0 }}>
        <div style={{ fontSize: "10px", fontWeight: 700, color: "#aaa", letterSpacing: "0.08em", marginBottom: "3px" }}>ACCESS LEVEL</div>
        <div style={{ fontSize: "14px", fontWeight: 600, color: "#111" }}>{accessLevel}</div>
      </div>

      {/* ACTIONS ALLOWED */}
      <div style={{ flex: 1, minHeight: 0, marginBottom: "14px", display: "flex", flexDirection: "column" }}>
        <div style={{ fontSize: "10px", fontWeight: 700, color: "#aaa", letterSpacing: "0.08em", marginBottom: "6px", flexShrink: 0 }}>ACTIONS ALLOWED</div>
        <div style={{
          border: "1px solid #E5E7EB",
          borderRadius: "10px",
          overflow: "hidden",
          overflowY: "auto",
          flex: 1,
          minHeight: 0,
        }}>
          {Object.entries(actionGroups).map(([cat, perms], gi) => (
            <div key={cat}>
              <div style={{
                padding: "5px 12px",
                fontSize: "10px",
                fontWeight: 700,
                color: "#aaa",
                letterSpacing: "0.08em",
                background: gi % 2 === 0 ? "#FAFAFA" : "#fff",
                borderBottom: "1px solid #F0F0F0",
                position: "sticky",
                top: 0,
              }}>
                {formatLabel(cat).toUpperCase()}
              </div>
              {perms.map((p) => {
                const isSelected = selectedIds?.includes(p._id);
                return (
                  <div
                    key={p._id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "7px 12px",
                      background: isSelected ? "#FFF7ED" : "#fff",
                      borderBottom: "1px solid #F5F5F5",
                    }}
                  >
                    <MdCheck style={{
                      fontSize: "15px",
                      color: isSelected ? "#F97316" : "#D1D5DB",
                      flexShrink: 0,
                    }} />
                    <span style={{ fontSize: "13px", color: isSelected ? "#111" : "#9CA3AF" }}>
                      {p.description || `${formatLabel("View")} ${moduleName}`}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Close button */}
      <div style={{ display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
        <button
          onClick={onClose}
          style={{
            padding: "7px 22px",
            borderRadius: "8px",
            border: "1px solid #E5E7EB",
            background: "#fff",
            fontSize: "13px",
            fontWeight: 600,
            color: "#333",
            cursor: "pointer",
          }}
        >
          Close
        </button>
      </div>
    </div>
  );

  return ReactDOM.createPortal(panel, document.body);
}

export default function PermissionSelector({
  forRoles,
  permissions,
  onDone = () => {},
}) {
  const [state, setState] = useState([]);
  const [openInfoId, setOpenInfoId] = useState(null);
  const [anchorRect, setAnchorRect] = useState(null);
  const [searchPerm, setSearchPerm] = useState({
    group: "",
    permission: "",
  });

  const [searchGroup, setSearchGroup] = useState("");
  const [isClosed, setIsClosed] = useState(false);
  const {
    permissions_data: { permissions: all_permissions },
  } = adminService.GetPermissions({ getAll: true });

  const MaxWidth991 = useMediaPredicate("(max-width: 991px)");

  const data = useMemo(
    () =>
      Object.entries(
        all_permissions?.reduce((acc, permission) => {
          const group = permission?.can?.split(".")[0];
          if (!acc[group]) {
            acc[group] = [];
          }
          acc[group].push(permission);
          return acc;
        }, {})
      )
        .filter(
          ([group]) =>
            searchGroup.toLowerCase() === "" ||
            group.toLowerCase()?.includes(searchGroup.toLowerCase())
        )
        .map(([group, _]) => ({
          label: group,
          content: (
            <PermissionListGroup>
              <PermissionListHead>
                <Field
                  sm
                  type="text"
                  noMargin
                  suffix={<i className="material-icons-round">search</i>}
                  placeholder="Search Permissions"
                  className="search"
                  onChange={({ target: { value } }) => {
                    setSearchPerm({ group, permission: value });
                  }}
                />
                <Switch
                  noMargin
                  label="Select All"
                  name={`${group}_all`}
                  className="switch-btn"
                  value={_.every(({ _id }) =>
                    state?.find(({ _id: __id }) => __id === _id)
                  )}
                  onChange={({ target: { value } }) => {
                    setState((s) =>
                      value
                        ? s.concat(
                            _.filter(
                              ({ _id }) =>
                                !s?.find(({ _id: __id }) => __id === _id)
                            )
                          )
                        : s.filter(
                            ({ _id }) =>
                              !_?.find(({ _id: __id }) => __id === _id)
                          )
                    );
                  }}
                />
              </PermissionListHead>
              <Grid
                xs={1}
                sm={2}
                lg={3}
                colGap={30}
                rowGap={20}
                css={`
                  padding: 15px;
                  overflow-x: hidden;
                  overflow-y: auto;
                  max-height: 500px;
                `}
              >
                {_.filter(({ can }) =>
                  group.toLowerCase().trim() ===
                  searchPerm.group.toLowerCase().trim()
                    ? can
                        .toLowerCase()
                        .trim()
                        .includes(searchPerm.permission.toLowerCase().trim())
                    : true
                ).map(({ _id, can }) => (
                  <GridCol key={_id}>
                    {/* Row: checkbox label + info icon side by side */}
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Field
                          noMargin
                          type="checkbox"
                          label={can}
                          name={_id}
                          value={!!state.find((c) => c._id === _id)}
                          onChange={({ target: { value } }) => {
                            setState((__) =>
                              value
                                ? [...__, { _id, can }]
                                : __.filter(({ _id: __id }) => __id !== _id)
                            );
                          }}
                        />
                      </div>
                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (openInfoId === _id) {
                            setOpenInfoId(null);
                            setAnchorRect(null);
                          } else {
                            setAnchorRect(e.currentTarget.getBoundingClientRect());
                            setOpenInfoId(_id);
                          }
                        }}
                        style={{
                          flexShrink: 0,
                          background: "none",
                          border: "none",
                          padding: "2px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          lineHeight: 1,
                          marginBottom: 0,
                        }}
                      >
                        <MdInfoOutline
                          style={{
                            fontSize: "16px",
                            color: openInfoId === _id ? "#F97316" : "#bbb",
                            transition: "color 0.15s",
                          }}
                        />
                      </button>
                    </div>
                  </GridCol>
                ))}
              </Grid>
            </PermissionListGroup>
          ),
        })),
    [state, searchPerm, searchGroup, openInfoId, all_permissions]
  );

  // Portal panel — rendered outside the overflow containers
  const activePermission = openInfoId
    ? all_permissions.find((p) => p._id === openInfoId)
    : null;

  const activeGroup = openInfoId && all_permissions.length
    ? (() => {
        const grp = activePermission?.can?.split(".")[0];
        return all_permissions.filter((p) => p.can.split(".")[0] === grp);
      })()
    : [];

  useEffect(() => {
    const selectedPermissions = all_permissions
      .filter(({ _id }) => permissions.includes(_id))
      .map(({ _id, can }) => ({ _id, can }));
    setState(selectedPermissions);
  }, [all_permissions, permissions, isClosed]);

  return (
    <>
    <PermissionSelectorWrapper>
      <ModalContainer
        width="1200"
        title="Customize Permissions"
        onModalClose={() => {
          setIsClosed(() => !isClosed);
        }}
        btnComponent={({ onClick }) => (
          <Button
            className="customize-btn"
            type="button"
            width="250px"
            variant="white"
            xs
            onClick={onClick}
            disabled={!permissions?.length && !forRoles}
          >
            Customize Permissions
            <FaArrowRightLong />
          </Button>
        )}
        content={({ onClose }) => (
          <>
            <PermissionListHead>
              <Field
                sm
                type="text"
                noMargin
                suffix={<i className="material-icons-round">search</i>}
                placeholder="Search Permission Group"
                className="search"
                onChange={({ target: { value } }) => {
                  setSearchGroup(value);
                }}
              />
              <Switch
                noMargin
                type="checkbox"
                label="Select All Groups Permissions"
                name="select_all_permissions"
                value={all_permissions?.length === state.length}
                onChange={({ target: { value } }) => {
                  if (value) {
                    setState(
                      all_permissions?.map(({ _id, can }) => ({ _id, can }))
                    );
                  } else {
                    setState([]);
                  }
                }}
              />
            </PermissionListHead>
            <DataTabs
              data={data}
              orientation={MaxWidth991 ? "horizontal" : "vertical"}
              isModal
            />
            <ConfirmButton>
              <Button
                xs
                // width="150px"
                type="button"
                disabled={!state.length}
                onClick={() => {
                  onClose();
                  onDone(state.map(({ can }) => can));
                }}
              >
                Confirm
              </Button>
            </ConfirmButton>
          </>
        )}
      />
    </PermissionSelectorWrapper>
    {openInfoId && activePermission && (
      <PermissionDetailsPanel
        permission={activePermission}
        groupPermissions={activeGroup}
        selectedIds={state.map((s) => s._id)}
        anchorRect={anchorRect}
        onClose={() => {
          setOpenInfoId(null);
          setAnchorRect(null);
        }}
      />
    )}
    </>
  );
}
